import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Pagination } from 'swiper';
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react';

import { useWindowSize } from 'hooks/useWindowSize';
import { Achievement, User } from 'types';

import { AchievementSection } from '../AchievementSection';
import { ListW, MobileListW, GridW, ListFooterW, SeeMoreW, DesktopListW } from './styled';

const MAX_PREVIEW_ACHIEVEMENTS = 3;

interface AchievementsListProps {
  data: Achievement[];
  employeeId: string;
  usersList: User[];
  isFullView?: boolean;
  isProfilePreview?: boolean;
  viewAllLink?: string;
}

export function AchievementsList({
  data,
  employeeId,
  usersList,
  isProfilePreview,
  isFullView = false,
  viewAllLink,
}: AchievementsListProps): ReactElement {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();

  const items = isProfilePreview && !isFullView ? data.slice(0, MAX_PREVIEW_ACHIEVEMENTS) : data;
  const remaining = data.length - items.length;
  const showFooter = !!(isProfilePreview && !isFullView && viewAllLink);

  const footer = showFooter ? (
    <ListFooterW>
      <SeeMoreW onClick={() => navigate(viewAllLink as string)}>
        {remaining > MAX_PREVIEW_ACHIEVEMENTS
          ? `+ ${remaining} more`
          : t('career.achievements.seeAllGoals')}
      </SeeMoreW>
    </ListFooterW>
  ) : null;

  const itemElements = isMobile ? (
    <SwiperComponent pagination modules={[Pagination]}>
      {items.map(({ id, title, description, color, goals, progress, owner_id }) => (
        <SwiperSlide key={id}>
          <AchievementSection
            id={id}
            title={title}
            color={color}
            progress={progress}
            description={description}
            goals={goals}
            employeeId={employeeId}
            isFullView={isFullView}
            isProfilePreview={isProfilePreview}
            ownerId={owner_id}
            usersList={usersList}
          />
        </SwiperSlide>
      ))}
    </SwiperComponent>
  ) : isFullView ? (
    <DesktopListW>
      {items.map(({ id, title, description, color, goals, progress, owner_id }) => (
        <AchievementSection
          key={id}
          id={id}
          title={title}
          color={color}
          progress={progress}
          description={description}
          goals={goals}
          employeeId={employeeId}
          isFullView={isFullView}
          isProfilePreview={isProfilePreview}
          ownerId={owner_id}
          usersList={usersList}
        />
      ))}
    </DesktopListW>
  ) : (
    <>
      {items.map(({ id, title, description, color, goals, progress, owner_id }) => (
        <AchievementSection
          key={id}
          id={id}
          title={title}
          color={color}
          progress={progress}
          description={description}
          goals={goals}
          employeeId={employeeId}
          isFullView={isFullView}
          isProfilePreview={isProfilePreview}
          ownerId={owner_id}
          usersList={usersList}
        />
      ))}
    </>
  );

  if (isMobile) {
    return (
      <MobileListW isBackground={isFullView}>
        {itemElements}
        {footer}
      </MobileListW>
    );
  }

  if (isProfilePreview && !isFullView) {
    return (
      <GridW>
        <ListW isFullView={false}>{itemElements}</ListW>
        {footer}
      </GridW>
    );
  }

  return <ListW isFullView={isFullView}>{itemElements}</ListW>;
}
